/// 0.2.1 - Customização do Autocomplete, com geocoding para obter o número e detalhes adicionais
import React, { useEffect, useRef, useState } from 'react';
import { notification } from 'antd';
import { CheckCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import styled from 'styled-components';

const AutocompleteContainer = styled.div`
  position: relative;
  width: 100%;
`;

const SuggestionsList = styled.ul`
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  background: white;
  border: 1px solid #ccc;
  border-top: none;
  z-index: 1000;
  list-style: none;
  padding: 0;
  margin: 0;
  box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.1);
`;

const SuggestionItem = styled.li`
  padding: 8px;
  cursor: pointer;
  &:hover {
    background: #f0f0f0;
  }
`;

const AddressResult = styled.div`
  /* margin-top: 8px;
  background: #f9f9f9;
  padding: 8px;
  border-radius: 4px; */
  display: flex;
  /* align-items: center; */
  gap: 8px;
`;

function AddressAutocomplete({ uf, city, number, currentPlace, onAddressSelect }) {
  const inputRef = useRef(null);
  const googleInputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const [query, setQuery] = useState('');
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [isValid, setIsValid] = useState(null);

  useEffect(() => {
    if (!query && currentPlace && currentPlace.formatted_address) {
      setQuery(currentPlace.formatted_address);
    }
  }, []);

  useEffect(() => {
    if (!window.google || !window.google.maps) {
      console.error('Google Maps API não carregada');
      return;
    }

    const autocomplete = new window.google.maps.places.Autocomplete(googleInputRef.current, {
      types: ['address'],
      componentRestrictions: { country: 'br' },
      fields: ['address_components', 'formatted_address', 'geometry'],
    });
    autocompleteRef.current = autocomplete;

    const handlePlaceChanged = () => {
      const place = autocomplete.getPlace();
      if (!place.geometry) {
        notification.error({
          key: 'addressError',
          message: 'Erro',
          description: 'Nenhum endereço válido foi encontrado.',
        });
        setIsValid(false);
        return;
      }
      mapPlaceToAddress(place);

    };

    const listener = autocomplete.addListener('place_changed', handlePlaceChanged);

    return () => {
      if (listener && window.google && window.google.maps) {
        window.google.maps.event.removeListener(listener);
      }
    };
  }, [uf, city, number, onAddressSelect]);

  const mapPlaceToAddress = (place) => {
    if (!place || !place.address_components || place.address_components.length === 0 || !place.geometry?.location) {
      console.error('Endereço inválido ou sem address_components:', place);
      notification.error({
        key: 'addressError',
        message: 'Erro',
        description: 'O endereço selecionado não contém informações completas.',
      });
      setSelectedAddress(null);
      onAddressSelect(null);
      setIsValid(false);
      return;
    }

    const addressData = {
      formatted_address: place.formatted_address,
      location: {
        latitude: place.geometry?.location?.lat(),
        longitude: place.geometry?.location?.lng(),
      },
    };

    // Percorre os address_components para buscar as informações corretas
    place.address_components.forEach((component) => {
      const types = component.types || [];

      if (types.includes('street_number')) {
        addressData.addressNumber = component.long_name;
      }
      if (types.includes('route')) {
        addressData.address = component.long_name;
      }
      if (types.includes('sublocality_level_1') || types.includes('neighborhood')) {
        addressData.neighborhoodName = component.long_name;
      }
      if (types.includes('locality') || types.includes('administrative_area_level_2')) {
        addressData.cityName = component.long_name;
      }
      if (types.includes('administrative_area_level_1')) {
        addressData.stateName = component.short_name;
      }
      if (types.includes('country')) {
        addressData.countryName = component.long_name;
      }
      if (types.includes('postal_code')) {
        addressData.postalCode = component.long_name;
      }
    });

    if (!addressData.addressNumber && number)
      addressData.addressNumber = number;
    // Validação: endereço precisa ter um número
    if (!addressData.addressNumber) {
      console.error('Endereço sem número:', place);
      notification.error({
        key: 'addressNumberError',
        message: 'Erro',
        description: 'Numero não encontrado no endereço selecionado.',
      });
      setSelectedAddress(null);
      onAddressSelect(null);
      setIsValid(false);
      return;
    }

    setSelectedAddress(addressData);
    onAddressSelect(addressData);
    setQuery(place.formatted_address);
    setSuggestions([]);
    setIsValid(true);
  };

  const handleChange = (e) => {
    const inputValue = e.target.value;
    setQuery(inputValue);
    googleInputRef.current.value = inputValue;

    if (autocompleteRef.current) {
      setSelectedAddress(null);
      setIsValid(null);
      onAddressSelect(null);
      const service = new window.google.maps.places.AutocompleteService();
      const searchInput = `${inputValue}, ${city}, ${uf}, ${number}`;
      service.getPlacePredictions({ input: searchInput, componentRestrictions: { country: 'br' } }, (predictions) => {
        if (predictions && predictions.length > 0) {
          setSuggestions([predictions[0].description]);
        } else {
          setSuggestions([]);
          setIsValid(false);
        }
      });
    }
  };

  const handleSelectSuggestion = (suggestion) => {
    setQuery(suggestion);
    setSuggestions([]);
    googleInputRef.current.value = suggestion;
    if (autocompleteRef.current) {
      const placesService = new window.google.maps.places.PlacesService(document.createElement('div'));
      placesService.findPlaceFromQuery(
        {
          query: suggestion,
          fields: ['place_id', 'formatted_address', 'geometry'], // Removido 'address_components'
        },
        (results, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && results.length > 0) {
            const place = results[0];

            // Após encontrar o Place ID, chamamos getDetails para obter os address_components
            placesService.getDetails(
              {
                placeId: place.place_id,
                fields: ['address_components', 'formatted_address', 'geometry'],
              },
              (detailedPlace, detailsStatus) => {
                if (detailsStatus === window.google.maps.places.PlacesServiceStatus.OK) {
                  mapPlaceToAddress(detailedPlace);
                } else {
                  console.error('Erro ao buscar detalhes do endereço:', detailsStatus);
                  setSelectedAddress(null);
                  onAddressSelect(null);
                  setIsValid(false);
                }
              }
            );
          } else {
            console.error('Erro na busca de endereço:', status);
            setSelectedAddress(null);
            onAddressSelect(null);
            setIsValid(false);
          }
        }
      );

    }
  };

  const handleBlur = () => {
    if (suggestions.length > 0) {
      handleSelectSuggestion(suggestions[0]);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && suggestions.length > 0) {
      handleSelectSuggestion(suggestions[0]);
    }
  };

  return (
    <AutocompleteContainer>
      <AddressResult>
        {isValid != null && (
          isValid ? <CheckCircleOutlined style={{ color: 'green' }} /> : <ExclamationCircleOutlined style={{ color: 'red' }} />)
        }
        <input
          type="text"
          value={query}
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          placeholder="Digite o endereço"
          autoComplete="new-address"
        />
      </AddressResult>
      <input ref={googleInputRef} type="text" style={{ display: 'none' }} />
      {suggestions.length > 0 && (
        <SuggestionsList>
          <SuggestionItem onClick={() => handleSelectSuggestion(suggestions[0])}>
            {suggestions[0]}
          </SuggestionItem>
        </SuggestionsList>
      )}

    </AutocompleteContainer>
  );
}

export default AddressAutocomplete;

